import { useState } from 'react';
import { useMutation } from 'react-apollo';

import {
  rosterIdentityLinkedIdentityCreate as rosterIdentityLinkedIdentityCreateMutation,
  rosterIdentityLinkedIdentityUpdate as rosterIdentityLinkedIdentityUpdateMutation,
  rosterIdentityUpdate as rosterIdentityUpdateMutation,
} from '../../../../../../../graphql/mutations/roster-identity';
import { RosterIdentityForContactInfoEdits } from '../../../../../../../graphql/queries/roster-identitiy';
import {
  getErrorString,
  getGraphQLErrorCodes,
} from '../../../../../../../util/normalize-errors';
import { CONTACT_METHOD_ERROR, EMAIL_ERROR_KEY } from '../constants';

/**
 * Maps a platform error onto the row it belongs to, so the modal can show it
 * against the offending input instead of as a modal-wide alert.
 *
 * @param {Error} error The error thrown by the mutation.
 * @returns {String} The message to display.
 */
const getContactMethodErrorMessage = error => {
  // Match on the code rather than the text: getErrorString resolves to a
  // human-readable message, so the code never survives into it.
  const codes = getGraphQLErrorCodes(error);

  if (codes.includes(CONTACT_METHOD_ERROR.PHONE_TAKEN)) {
    return 'This phone number is already in use. Please provide a different one.';
  }

  if (codes.includes(CONTACT_METHOD_ERROR.EMAIL_TAKEN)) {
    return 'This email address is already on this member.';
  }

  if (codes.includes(CONTACT_METHOD_ERROR.MARKETING_PHONE)) {
    return 'This account cannot have its phone number updated.';
  }

  if (codes.includes(CONTACT_METHOD_ERROR.NO_ACCESS)) {
    return 'This contact method can no longer be updated.';
  }

  return getErrorString(error);
};

/**
 * Saves contact method changes on a roster identity.
 *
 * Phone numbers live on the account behind each linked identity and are saved
 * one row at a time, so a rejected number does not discard the others. Email
 * addresses belong to the roster identity and batch into a single call.
 *
 * @param {Object} options The options object.
 * @param {String} options.rosterIdentityId The roster identity being edited.
 * @returns {Object} The update handlers and state.
 */
const useRosterIdentityContactMethodUpdate = ({ rosterIdentityId } = {}) => {
  const [rosterIdentityLinkedIdentityUpdate] = useMutation(
    rosterIdentityLinkedIdentityUpdateMutation,
  );
  const [rosterIdentityLinkedIdentityCreate] = useMutation(
    rosterIdentityLinkedIdentityCreateMutation,
  );
  const [rosterIdentityUpdate] = useMutation(rosterIdentityUpdateMutation);

  const [updating, setUpdating] = useState(false);
  const [rowErrors, setRowErrors] = useState({});

  const refetchQueries = rosterIdentityId
    ? [
        {
          query: RosterIdentityForContactInfoEdits,
          variables: { roster_identity_ids: [rosterIdentityId] },
        },
      ]
    : [];

  /**
   * @typedef {Object} PhoneChange
   * @property {String} linkedIdentityId The linked identity to update.
   * @property {String} phoneNumber The new phone number.
   */

  /**
   * @typedef {Object} EmailChanges
   * @property {Array<Object>} updated Existing rows, as { id, emailAddress }.
   * @property {Array<String>} created New email addresses.
   * @property {Array<String>} deleted Ids of rows to remove.
   */

  /**
   * Applies a set of contact method changes.
   *
   * @param {Object} changes The changes to apply.
   * @param {Array<PhoneChange>} [changes.phoneChanges] The phone changes.
   * @param {Array<Object>} [changes.phoneCreations] New numbers, as
   *   { key, phoneNumber }, each getting its own linked identity.
   * @param {EmailChanges} [changes.emailChanges] The email changes.
   * @returns {Promise<Object>} The outcome, as { success, errors }.
   */
  const onContactMethodsUpdate = async ({
    phoneChanges = [],
    phoneCreations = [],
    emailChanges,
  } = {}) => {
    const hasEmailChanges =
      (emailChanges?.updated?.length ?? 0) > 0 ||
      (emailChanges?.created?.length ?? 0) > 0 ||
      (emailChanges?.deleted?.length ?? 0) > 0;

    if (
      phoneChanges.length === 0 &&
      phoneCreations.length === 0 &&
      !hasEmailChanges
    ) {
      return { success: true, errors: {} };
    }

    setUpdating(true);
    setRowErrors({});

    const errors = {};

    // Sequential rather than parallel: these all touch the same roster
    // identity, and a partial failure is easier to report in order.
    for (const { linkedIdentityId, phoneNumber } of phoneChanges) {
      try {
        await rosterIdentityLinkedIdentityUpdate({
          variables: {
            input: {
              roster_identity_linked_identity_id: linkedIdentityId,
              phone_number: phoneNumber,
            },
          },
          refetchQueries,
        });
      } catch (error) {
        errors[linkedIdentityId] = getContactMethodErrorMessage(error);
      }
    }

    // A new number needs a linked identity of its own to hang on. The platform
    // returns the existing one when the member already carries that number, so
    // adding a duplicate is a no-op rather than an error.
    for (const { key, phoneNumber } of phoneCreations) {
      try {
        await rosterIdentityLinkedIdentityCreate({
          variables: {
            input: {
              roster_identity_id: rosterIdentityId,
              linked_identity: { phone_number: phoneNumber },
            },
          },
          refetchQueries,
        });
      } catch (error) {
        errors[key] = getContactMethodErrorMessage(error);
      }
    }

    if (hasEmailChanges) {
      try {
        await rosterIdentityUpdate({
          variables: {
            input: {
              roster_identity_id: rosterIdentityId,
              update_email_addresses: (emailChanges.updated ?? []).map(
                ({ id, emailAddress }) => ({
                  id,
                  email_address: emailAddress,
                }),
              ),
              create_email_addresses: emailChanges.created ?? [],
              delete_email_address_ids: emailChanges.deleted ?? [],
            },
          },
          refetchQueries,
        });
      } catch (error) {
        // Emails batch into one call, so an failure belongs to the section
        // rather than to any single row.
        errors[EMAIL_ERROR_KEY] = getContactMethodErrorMessage(error);
      }
    }

    setRowErrors(errors);
    setUpdating(false);

    // The errors come back with the result rather than only through state, so
    // the caller can drop the local edits it just persisted without waiting
    // for a re-render.
    return { success: Object.keys(errors).length === 0, errors };
  };

  return {
    updating,
    rowErrors,
    onContactMethodsUpdate,
  };
};

export default useRosterIdentityContactMethodUpdate;
